import { Box, Stack } from "@mui/material";
import { AppSkeletonCircle } from "@src/appV2/redesign/components/AppSkeleton/AppSkeletonCircle";
import { AppSkeletonText } from "@src/appV2/redesign/components/AppSkeleton/AppSkeletonText";
import { Avatar } from "@src/appV2/redesign/components/Avatar";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { DialogHeader } from "@src/appV2/redesign/components/DialogHeader";
import { Divider } from "@src/appV2/redesign/components/Divider";
import { SearchInput } from "@src/appV2/redesign/components/SearchInput/SearchInput";
import { SelectableListItem } from "@src/appV2/redesign/components/SelectableListItem/SelectableListItem";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useMemo, useState } from "react";

import { type AccountSwitcherUser, type UserDetails } from "../../Shared/AccountSwitcher/types";

const SEARCH_THRESHOLD = 5;
const SKELETON_ROW_COUNT = 4;

interface WorkplaceSelectorSheetProps {
  modalState: UseModalState;
  workplaceDetails: UserDetails;
  selectedWorkplaceId?: string;
  onWorkplaceSelected: (user: AccountSwitcherUser) => void;
}

export function WorkplaceSelectorSheet(props: WorkplaceSelectorSheetProps) {
  const { modalState, workplaceDetails, selectedWorkplaceId, onWorkplaceSelected } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const showSearch = workplaceDetails.list.length > SEARCH_THRESHOLD;

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setSearchTerm("");
    }
  }, [modalState.modalIsOpen]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const selectedWorkplace = useMemo(
    () =>
      selectedWorkplaceId
        ? workplaceDetails.list.find((workplace) => workplace.userId === selectedWorkplaceId)
        : undefined,
    [workplaceDetails.list, selectedWorkplaceId]
  );

  const otherWorkplaces = useMemo(
    () =>
      workplaceDetails.list
        .filter((workplace) => workplace.userId !== selectedWorkplaceId)
        .filter((workplace) =>
          normalizedSearch ? workplace.title.toLowerCase().includes(normalizedSearch) : true
        ),
    [workplaceDetails.list, selectedWorkplaceId, normalizedSearch]
  );

  const filteredSelectedWorkplace =
    selectedWorkplace &&
    (normalizedSearch.length === 0 ||
      selectedWorkplace.title.toLowerCase().includes(normalizedSearch))
      ? selectedWorkplace
      : undefined;

  const hasResults = otherWorkplaces.length > 0 || Boolean(filteredSelectedWorkplace);

  return (
    <BottomSheet modalState={modalState} maxHeightPercent={85} minHeight="24rem">
      <Stack sx={{ height: "100%" }}>
        <DialogHeader
          title="Switch workplace"
          onClose={() => {
            modalState.closeModal();
          }}
        />

        {showSearch && (
          <Box sx={{ paddingX: 5, paddingTop: 3 }}>
            <SearchInput
              value={searchTerm}
              placeholder="Search workplaces..."
              onChange={setSearchTerm}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            paddingTop: 3,
            paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)",
          }}
        >
          <SheetBody
            loading={workplaceDetails.loading}
            hasResults={hasResults}
            searchTerm={searchTerm}
            selectedWorkplace={filteredSelectedWorkplace}
            otherWorkplaces={otherWorkplaces}
            onWorkplaceSelected={onWorkplaceSelected}
          />
        </Box>
      </Stack>
    </BottomSheet>
  );
}

interface SheetBodyProps {
  loading: boolean;
  hasResults: boolean;
  searchTerm: string;
  selectedWorkplace?: AccountSwitcherUser;
  otherWorkplaces: AccountSwitcherUser[];
  onWorkplaceSelected: (user: AccountSwitcherUser) => void;
}

function SheetBody(props: SheetBodyProps) {
  const {
    loading,
    hasResults,
    searchTerm,
    selectedWorkplace,
    otherWorkplaces,
    onWorkplaceSelected,
  } = props;

  if (loading) {
    return <SkeletonList />;
  }

  if (!hasResults) {
    return <EmptyState searchTerm={searchTerm} />;
  }

  return (
    <Stack>
      {selectedWorkplace && (
        <>
          <SectionLabel>Current</SectionLabel>
          <WorkplaceRow workplace={selectedWorkplace} isSelected onSelect={onWorkplaceSelected} />
          {otherWorkplaces.length > 0 && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <SectionLabel>Other workplaces</SectionLabel>
            </>
          )}
        </>
      )}
      {otherWorkplaces.map((workplace) => (
        <WorkplaceRow
          key={workplace.userId}
          workplace={workplace}
          isSelected={false}
          onSelect={onWorkplaceSelected}
        />
      ))}
    </Stack>
  );
}

interface WorkplaceRowProps {
  workplace: AccountSwitcherUser;
  isSelected: boolean;
  onSelect: (workplace: AccountSwitcherUser) => void;
}

function WorkplaceRow(props: WorkplaceRowProps) {
  const { workplace, isSelected, onSelect } = props;

  return (
    <SelectableListItem
      indicatorPosition="right"
      isSelected={isSelected}
      selectionMode="radio"
      onSelect={() => {
        onSelect(workplace);
      }}
    >
      <Stack direction="row" spacing={3} sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
        <Avatar alt={workplace.title} size="small" />
        <Text
          semibold={isSelected}
          variant="body1"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {workplace.title}
        </Text>
      </Stack>
    </SelectableListItem>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      variant="overline"
      sx={(theme) => ({
        paddingX: 4,
        paddingY: 2,
        color: theme.palette.base?.muted,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      })}
    >
      {children}
    </Text>
  );
}

function SkeletonList() {
  return (
    <Stack gap={2} sx={{ paddingX: 4 }}>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        <Stack
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          direction="row"
          alignItems="center"
          gap={3}
          sx={{ minHeight: "3.5rem" }}
        >
          <AppSkeletonCircle size="1.875rem" />
          <AppSkeletonText lineCount={1} lineHeight={16} widths={["80%"]} sx={{ flex: 1 }} />
          <AppSkeletonCircle size="1.25rem" />
        </Stack>
      ))}
    </Stack>
  );
}

function EmptyState({ searchTerm }: { searchTerm: string }) {
  const trimmed = searchTerm.trim();
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "12rem", paddingX: 4 }}>
      <Text
        variant="body2"
        sx={(theme) => ({ color: theme.palette.base?.muted, textAlign: "center" })}
      >
        {trimmed ? `No workplaces match "${trimmed}"` : "No workplaces available"}
      </Text>
    </Stack>
  );
}
